import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import ReportPaginator from '../components/ReportPaginator';
import ScreenHeader from '../components/layout/ScreenHeader';
import { Button as CmButton, FilterCard, StatCard, StatGrid } from '../ui';
import { Building2, CalendarDays, RotateCw, Timer as TimerLucide } from 'lucide-react';
import { History as HistoryLucide, Pause as PauseLucide, Play as PlayLucide, RefreshCw, Square } from 'lucide-react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    Chip,
    Avatar,
    CircularProgress,
    Paper,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    Tooltip,
    Badge,
    Collapse,
} from '@mui/material';
import {
    Timer as TimerIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Business as ClubIcon,
    Person as PersonIcon,
    TableRestaurant as TableIcon,
    History as HistoryIcon,
    AccessTime as AccessTimeIcon,
    TrendingUp as TrendingUpIcon,
    Sync as SyncIcon,
} from '@mui/icons-material';

let API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000';
if (API_URL.endsWith('/api')) {
    API_URL = API_URL.slice(0, -4);
}
if (API_URL.endsWith('/')) {
    API_URL = API_URL.slice(0, -1);
}

const TimeTracking = () => {
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    // State
    const [loading, setLoading] = useState(true);
    const [timeData, setTimeData] = useState(null);
    const [expandedClubs, setExpandedClubs] = useState({});
    const [selectedTab, setSelectedTab] = useState(0);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

    // History state
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedClubForHistory, setSelectedClubForHistory] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyFilters, setHistoryFilters] = useState({
        startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        page: 1,
        limit: 20,
    });

    // Sync state
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncResultModalOpen, setSyncResultModalOpen] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    // All Sessions History state
    const [allHistoryData, setAllHistoryData] = useState(null);
    const [allHistoryLoading, setAllHistoryLoading] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [allHistoryFilters, setAllHistoryFilters] = useState({
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        clubId: '',
        page: 1,
        limit: 20,
    });

    // Fetch time tracking data
    const fetchTimeTracking = useCallback(async () => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.get(`${API_URL}/api/dealer/time-tracking`, config);
            setTimeData(data.data);
            setLastUpdatedAt(new Date());

            // Auto-expand all clubs on first load
            if (Object.keys(expandedClubs).length === 0 && data.data.clubs?.length > 0) {
                const expanded = {};
                data.data.clubs.forEach(club => {
                    expanded[club.clubId] = true;
                });
                setExpandedClubs(expanded);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load time tracking data');
        } finally {
            setLoading(false);
        }
    }, [userInfo, expandedClubs]);

    // Fetch session history
    const fetchSessionHistory = useCallback(async (clubId) => {
        if (!clubId) return;

        try {
            setHistoryLoading(true);
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.get(
                `${API_URL}/api/dealer/session-history?clubId=${clubId}&startDate=${historyFilters.startDate}&endDate=${historyFilters.endDate}&page=${historyFilters.page}&limit=${historyFilters.limit}`,
                config
            );
            setHistoryData(data.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load session history');
        } finally {
            setHistoryLoading(false);
        }
    }, [userInfo, historyFilters]);

    // Control session (pause/resume)
    const handleControlSession = async (playerId, action) => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            await axios.post(
                `${API_URL}/api/dealer/control-player-session`,
                { playerId, action },
                config
            );
            toast.success(`Session ${action}ed successfully`);
            fetchTimeTracking();
        } catch (error) {
            toast.error(error.response?.data?.message || `Failed to ${action} session`);
        }
    };

    // Sync sessions for seated players without sessions
    const handleSyncSessions = async () => {
        try {
            setSyncLoading(true);
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.post(
                `${API_URL}/api/dealer/sync-sessions`,
                {},
                config
            );
            setSyncResult(data.data);
            setSyncResultModalOpen(true);

            if (data.data.newSessionsCreated > 0) {
                toast.success(`Created ${data.data.newSessionsCreated} new sessions`);
                fetchTimeTracking();
            } else {
                toast.info('All seated players already have sessions');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to sync sessions');
        } finally {
            setSyncLoading(false);
        }
    };

    // Fetch all sessions history
    const fetchAllSessionsHistory = useCallback(async () => {
        try {
            setAllHistoryLoading(true);
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            let url = `${API_URL}/api/dealer/all-sessions-history?startDate=${allHistoryFilters.startDate}&endDate=${allHistoryFilters.endDate}&page=${allHistoryFilters.page}&limit=${allHistoryFilters.limit}`;
            if (allHistoryFilters.clubId) {
                url += `&clubId=${allHistoryFilters.clubId}`;
            }
            const { data } = await axios.get(url, config);
            setAllHistoryData(data.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load sessions history');
        } finally {
            setAllHistoryLoading(false);
        }
    }, [userInfo, allHistoryFilters]);

    // Initial fetch and auto-refresh
    useEffect(() => {
        fetchTimeTracking();

        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchTimeTracking, 30000);

        return () => {
            if (interval) clearInterval(interval);
        };
    }, []);

    // Fetch history when modal opens or filters change
    useEffect(() => {
        if (historyModalOpen && selectedClubForHistory) {
            fetchSessionHistory(selectedClubForHistory);
        }
    }, [historyModalOpen, selectedClubForHistory, historyFilters, fetchSessionHistory]);

    // Fetch all sessions history when tab 2 is selected or filters change
    useEffect(() => {
        if (selectedTab === 2) {
            fetchAllSessionsHistory();
        }
    }, [selectedTab, allHistoryFilters, fetchAllSessionsHistory]);

    const toggleClubExpand = (clubId) => {
        setExpandedClubs(prev => ({
            ...prev,
            [clubId]: !prev[clubId]
        }));
    };

    const openHistoryModal = (club) => {
        setSelectedClubForHistory(club.clubId);
        setHistoryModalOpen(true);
    };

    // Format elapsed time with live update display
    const formatElapsedTime = (session) => {
        const hours = Math.floor(session.elapsedMinutes / 60);
        const minutes = session.elapsedMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const historyClub = allHistoryData?.clubs?.find((club) => club._id === allHistoryFilters.clubId);
    const historyFilterChips = [
        (allHistoryFilters.startDate || allHistoryFilters.endDate) && {
            icon: CalendarDays,
            label: `${allHistoryFilters.startDate || '…'} → ${allHistoryFilters.endDate || '…'}`,
        },
        allHistoryFilters.clubId && { icon: Building2, label: historyClub ? `${historyClub.name} (${historyClub.clubCode})` : 'Club' },
    ].filter(Boolean);

    return (
        <Box>
            <ScreenHeader
                title="Time tracking"
                description="Every running and paused player session across your clubs, updated live."
                actions={
                    <>
                        <span className="text-xs text-cm-muted tabular-nums">
                            Updated {lastUpdatedAt ? moment(lastUpdatedAt).format('hh:mm:ss A') : '—'}
                        </span>
                        <CmButton variant="secondary" icon={RefreshCw} onClick={fetchTimeTracking}>Refresh</CmButton>
                        <CmButton
                            icon={RotateCw}
                            loading={syncLoading}
                            onClick={handleSyncSessions}
                            title="Create sessions for seated players who don't have one"
                        >
                            {syncLoading ? 'Syncing…' : 'Sync sessions'}
                        </CmButton>
                    </>
                }
            />

            <StatGrid className="mb-6">
                <StatCard label="Active sessions" icon={TimerLucide} value={timeData?.summary?.totalSessions || 0} hint="Running and paused" />
                <StatCard label="Running now" icon={PlayLucide} tone="success" value={timeData?.summary?.totalActiveSessions || 0} hint="Clock is ticking" />
                <StatCard label="Paused" icon={PauseLucide} tone={timeData?.summary?.totalPausedSessions ? 'warning' : undefined} value={timeData?.summary?.totalPausedSessions || 0} hint="Player away from table" />
                <StatCard label="Clubs with sessions" icon={Building2} value={timeData?.summary?.totalClubs || 0} />
            </StatGrid>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
                    <Tab label="Live Sessions" icon={<TimerIcon />} iconPosition="start" />
                    <Tab label="Club Overview" icon={<ClubIcon />} iconPosition="start" />
                    <Tab label="Session History" icon={<HistoryIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {selectedTab === 0 && (
                <Box>
                    {/* Club-wise sessions */}
                    {timeData?.clubs?.length > 0 ? (
                        timeData.clubs.map((club) => (
                            <Paper key={club.clubId} sx={{ mb: 3, overflow: 'hidden' }}>
                                {/* Club Header */}
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'var(--cm-red)',
                                        color: 'white',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => toggleClubExpand(club.clubId)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <ClubIcon />
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {club.clubName}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                Code: {club.clubCode}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip
                                            label={`${club.totalActive} Active`}
                                            sx={{ bgcolor: 'var(--cm-success)', color: 'white' }}
                                            size="small"
                                        />
                                        <Chip
                                            label={`${club.totalPaused} Paused`}
                                            sx={{ bgcolor: 'var(--cm-warning)', color: 'white' }}
                                            size="small"
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openHistoryModal(club);
                                            }}
                                            sx={{ color: 'white' }}
                                        >
                                            <Tooltip title="View History">
                                                <HistoryLucide size={19} />
                                            </Tooltip>
                                        </IconButton>
                                        {expandedClubs[club.clubId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </Box>
                                </Box>

                                {/* Sessions Table */}
                                <Collapse in={expandedClubs[club.clubId]}>
                                    <TableContainer>
                                        <Table>
                                            <TableHead sx={{ bgcolor: 'var(--cm-surface-2)' }}>
                                                <TableRow>
                                                    <TableCell><strong>Player</strong></TableCell>
                                                    <TableCell><strong>Table / Seat</strong></TableCell>
                                                    <TableCell><strong>Started At</strong></TableCell>
                                                    <TableCell><strong>Elapsed Time</strong></TableCell>
                                                    <TableCell><strong>Status</strong></TableCell>
                                                    <TableCell align="center"><strong>Actions</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {club.activeSessions?.map((session) => (
                                                    <TableRow
                                                        key={session.sessionId}
                                                        sx={{
                                                            bgcolor: session.status === 'paused' ? 'var(--cm-warning-soft)' : 'inherit',
                                                            '&:hover': { bgcolor: 'var(--cm-info-soft)' }
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'var(--cm-info)' }}>
                                                                    {session.userName?.[0]?.toUpperCase() || 'U'}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                        {session.userName || 'Unknown'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {session.userPhone}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <TableIcon sx={{ fontSize: 18, color: 'gray' }} />
                                                                <Typography variant="body2">
                                                                    {session.tableName || 'N/A'} - Seat {session.seatNumber || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {moment(session.sessionStart).format('hh:mm A')}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {moment(session.sessionStart).format('DD MMM YYYY')}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <TimerIcon sx={{ fontSize: 18, color: session.status === 'active' ? 'var(--cm-success)' : 'var(--cm-warning)' }} />
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: session.status === 'active' ? 'var(--cm-success)' : 'var(--cm-warning)'
                                                                    }}
                                                                >
                                                                    {formatElapsedTime(session)}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={session.status === 'active' ? 'Running' : 'Paused'}
                                                                color={session.status === 'active' ? 'success' : 'warning'}
                                                                size="small"
                                                                icon={session.status === 'active' ? <PlayLucide size={15} /> : <PauseLucide size={15} />}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                                {session.status === 'active' ? (
                                                                    <Tooltip title="Pause Session">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleControlSession(session.userId, 'pause')}
                                                                            sx={{ bgcolor: 'var(--cm-warning-soft)', color: 'var(--cm-warning)' }}
                                                                        >
                                                                            <PauseLucide size={18} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Tooltip title="Resume Session">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleControlSession(session.userId, 'resume')}
                                                                            sx={{ bgcolor: 'var(--cm-success-soft)', color: 'var(--cm-success)' }}
                                                                        >
                                                                            <PlayLucide size={18} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip title="Stop Session">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleControlSession(session.userId, 'stop')}
                                                                        sx={{ bgcolor: 'var(--cm-danger-soft)', color: 'var(--cm-danger)' }}
                                                                    >
                                                                        <Square size={17} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {club.activeSessions?.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                            <Typography color="textSecondary">
                                                                No active sessions in this club
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Collapse>
                            </Paper>
                        ))
                    ) : (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <TimerIcon sx={{ fontSize: 60, color: 'gray', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary">
                                No Active Sessions
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                There are no active player sessions at the moment
                            </Typography>
                        </Paper>
                    )}
                </Box>
            )}

            {selectedTab === 1 && (
                <Grid container spacing={3}>
                    {timeData?.clubs?.map((club) => (
                        <Grid item xs={12} sm={6} md={4} key={club.clubId}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: 'var(--cm-red)', width: 50, height: 50 }}>
                                            <ClubIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {club.clubName}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {club.clubCode}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'var(--cm-success-soft)', borderRadius: 1 }}>
                                                <Typography variant="h4" sx={{ color: 'var(--cm-success)', fontWeight: 'bold' }}>
                                                    {club.totalActive}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Active
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'var(--cm-warning-soft)', borderRadius: 1 }}>
                                                <Typography variant="h4" sx={{ color: 'var(--cm-warning)', fontWeight: 'bold' }}>
                                                    {club.totalPaused}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Paused
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<HistoryLucide size={18} />}
                                        sx={{ mt: 2 }}
                                        onClick={() => openHistoryModal(club)}
                                    >
                                        View History
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {(!timeData?.clubs || timeData.clubs.length === 0) && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <ClubIcon sx={{ fontSize: 60, color: 'gray', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary">
                                    No Clubs with Active Sessions
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Tab 2: Session History */}
            {selectedTab === 2 && (
                <Box>
                    {/* Filters */}
                    <FilterCard
                        id="session-history-filters"
                        className="mb-6"
                        inlineToggle
                        open={filtersOpen}
                        onOpenChange={setFiltersOpen}
                        chips={historyFilterChips}
                        emptyLabel="All clubs and dates"
                        aside={
                            <Button
                                variant="outlined"
                                startIcon={<RefreshCw size={18} />}
                                onClick={fetchAllSessionsHistory}
                                disabled={allHistoryLoading}
                            >
                                Refresh
                            </Button>
                        }
                    >
                        <div className="flex flex-wrap gap-4 items-center">
                            <TextField
                                type="date"
                                label="Start Date"
                                value={allHistoryFilters.startDate}
                                onChange={(e) => setAllHistoryFilters(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ minWidth: 150 }}
                            />
                            <TextField
                                type="date"
                                label="End Date"
                                value={allHistoryFilters.endDate}
                                onChange={(e) => setAllHistoryFilters(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ minWidth: 150 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Filter by Club</InputLabel>
                                <Select
                                    value={allHistoryFilters.clubId}
                                    label="Filter by Club"
                                    onChange={(e) => setAllHistoryFilters(prev => ({ ...prev, clubId: e.target.value, page: 1 }))}
                                >
                                    <MenuItem value="">All Clubs</MenuItem>
                                    {allHistoryData?.clubs?.map(club => (
                                        <MenuItem key={club._id} value={club._id}>
                                            {club.name} ({club.clubCode})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </FilterCard>

                    {allHistoryLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6} sm={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'var(--cm-info-soft)' }}>
                                        <Typography variant="h4" sx={{ color: 'var(--cm-info)', fontWeight: 'bold' }}>
                                            {allHistoryData?.summary?.totalSessions || 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Total Sessions
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'var(--cm-success-soft)' }}>
                                        <Typography variant="h4" sx={{ color: 'var(--cm-success)', fontWeight: 'bold' }}>
                                            {allHistoryData?.summary?.totalHours || 0}h
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Total Hours
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'var(--cm-warning-soft)' }}>
                                        <Typography variant="h4" sx={{ color: 'var(--cm-warning)', fontWeight: 'bold' }}>
                                            {allHistoryData?.summary?.uniqueUsersCount || 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Unique Users
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'var(--cm-gold-soft)' }}>
                                        <Typography variant="h4" sx={{ color: 'var(--cm-gold-text)', fontWeight: 'bold' }}>
                                            {allHistoryData?.summary?.uniqueClubsCount || 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Clubs
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Club Breakdown */}
                            {allHistoryData?.clubBreakdown?.length > 0 && (
                                <Paper sx={{ p: 2, mb: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Club-wise Breakdown
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {allHistoryData.clubBreakdown.map(club => (
                                            <Grid item xs={12} sm={6} md={4} key={club.clubId}>
                                                <Paper variant="outlined" sx={{ p: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                            {club.clubName}
                                                        </Typography>
                                                        <Chip label={club.clubCode} size="small" color="primary" variant="outlined" />
                                                    </Box>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="textSecondary">Total Hours:</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'var(--cm-success)' }}>
                                                            {club.totalHours}h
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="textSecondary">Sessions:</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {club.totalSessions}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="textSecondary">Unique Users:</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {club.uniqueUsersCount}
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            )}

                            {/* Sessions Table */}
                            <Paper>
                                <TableContainer>
                                    <Table>
                                        <TableHead sx={{ bgcolor: 'var(--cm-surface-2)' }}>
                                            <TableRow>
                                                <TableCell><strong>Player</strong></TableCell>
                                                <TableCell><strong>Club</strong></TableCell>
                                                <TableCell><strong>Date</strong></TableCell>
                                                <TableCell><strong>Start</strong></TableCell>
                                                <TableCell><strong>End</strong></TableCell>
                                                <TableCell><strong>Duration</strong></TableCell>
                                                <TableCell><strong>Status</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {allHistoryData?.sessions?.map((session) => (
                                                <TableRow key={session._id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'var(--cm-info)' }}>
                                                                {session.user?.name?.[0]?.toUpperCase() || 'U'}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                    {session.user?.name || 'Unknown'}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {session.user?.phoneNumber}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{session.club?.name || 'N/A'}</Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {session.club?.clubCode}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {moment(session.sessionStart).format('DD MMM YYYY')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {moment(session.sessionStart).format('hh:mm A')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {session.sessionEnd ? moment(session.sessionEnd).format('hh:mm A') : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={`${session.durationHours || 0}h`}
                                                            size="small"
                                                            sx={{ bgcolor: 'var(--cm-success-soft)', color: 'var(--cm-success)' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={session.status}
                                                            size="small"
                                                            color={session.status === 'completed' ? 'success' : 'warning'}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(!allHistoryData?.sessions || allHistoryData.sessions.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                        <HistoryIcon sx={{ fontSize: 50, color: 'gray', mb: 1 }} />
                                                        <Typography color="textSecondary">
                                                            No session history found for the selected period
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Pagination */}
                                {allHistoryData?.pagination && (
                                    <ReportPaginator
                                        first={(allHistoryFilters.page - 1) * allHistoryFilters.limit}
                                        rows={allHistoryFilters.limit}
                                        totalRecords={allHistoryData.pagination.total || 0}
                                        rowsPerPageOptions={[10, 20, 50, 100]}
                                        onPageChange={(event) =>
                                            setAllHistoryFilters(prev => ({ ...prev, page: event.page + 1, limit: event.rows }))
                                        }
                                    />
                                )}
                            </Paper>
                        </>
                    )}
                </Box>
            )}

            {/* History Modal */}
            <Dialog
                open={historyModalOpen}
                onClose={() => setHistoryModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon />
                        Session History - {historyData?.club?.name || 'Loading...'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {/* Filters */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
                        <TextField
                            type="date"
                            label="Start Date"
                            value={historyFilters.startDate}
                            onChange={(e) => setHistoryFilters(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        <TextField
                            type="date"
                            label="End Date"
                            value={historyFilters.endDate}
                            onChange={(e) => setHistoryFilters(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Box>

                    {historyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Player</strong></TableCell>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell><strong>Start Time</strong></TableCell>
                                        <TableCell><strong>End Time</strong></TableCell>
                                        <TableCell><strong>Duration</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historyData?.sessions?.map((session) => (
                                        <TableRow key={session._id}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {session.user?.name || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {session.user?.phoneNumber}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {moment(session.sessionStart).format('DD MMM YYYY')}
                                            </TableCell>
                                            <TableCell>
                                                {moment(session.sessionStart).format('hh:mm A')}
                                            </TableCell>
                                            <TableCell>
                                                {session.sessionEnd ? moment(session.sessionEnd).format('hh:mm A') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${session.durationHours || 0}h`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={session.status}
                                                    size="small"
                                                    color={session.status === 'completed' ? 'success' : 'warning'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(!historyData?.sessions || historyData.sessions.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                                <Typography color="textSecondary">
                                                    No session history found for the selected period
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                </DialogContent>
                {/* Pagination sits in the (non-scrolling) actions area so its rows-per-page
                    dropdown never gets clipped by DialogContent's overflow-y scroll region. */}
                {historyData?.pagination && (
                    <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                        <ReportPaginator
                            first={(historyFilters.page - 1) * historyFilters.limit}
                            rows={historyFilters.limit}
                            totalRecords={historyData.pagination.total || 0}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            onPageChange={(event) =>
                                setHistoryFilters(prev => ({ ...prev, page: event.page + 1, limit: event.rows }))
                            }
                        />
                    </Box>
                )}
                <DialogActions>
                    <Button onClick={() => setHistoryModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Sync Result Modal */}
            <Dialog
                open={syncResultModalOpen}
                onClose={() => setSyncResultModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SyncIcon sx={{ color: 'var(--cm-success)' }} />
                        Sync Sessions Result
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {syncResult && (
                        <Box>
                            {/* Summary Stats */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'var(--cm-info-soft)' }}>
                                        <Typography variant="h4" sx={{ color: 'var(--cm-info)', fontWeight: 'bold' }}>
                                            {syncResult.totalSeatedPlayers}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Seated Players
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'var(--cm-warning-soft)' }}>
                                        <Typography variant="h4" sx={{ color: 'var(--cm-warning)', fontWeight: 'bold' }}>
                                            {syncResult.alreadyHadSessions}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Already Had Sessions
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'var(--cm-success-soft)' }}>
                                        <Typography variant="h4" sx={{ color: 'var(--cm-success)', fontWeight: 'bold' }}>
                                            {syncResult.newSessionsCreated}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            New Sessions Created
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* New Sessions List */}
                            {syncResult.sessionsCreated?.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        Sessions Created:
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Player</strong></TableCell>
                                                    <TableCell><strong>Table</strong></TableCell>
                                                    <TableCell><strong>Seat</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {syncResult.sessionsCreated.map((session, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>
                                                            <Typography variant="body2">{session.playerName}</Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {session.playerPhone}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{session.tableName || 'N/A'}</TableCell>
                                                        <TableCell>{session.seatNumber || 'N/A'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {syncResult.newSessionsCreated === 0 && (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography color="textSecondary">
                                        All seated players already have active sessions.
                                    </Typography>
                                </Box>
                            )}

                            {/* Errors */}
                            {syncResult.errors?.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--cm-danger)', mb: 1 }}>
                                        Errors:
                                    </Typography>
                                    {syncResult.errors.map((err, idx) => (
                                        <Typography key={idx} variant="body2" color="error">
                                            {err.playerName}: {err.error}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSyncResultModalOpen(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TimeTracking;
